import { useNavigate } from "react-router";
import { Ban, CheckCircle2, ExternalLink, MoreVertical, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import {
  buildWhatsAppUrl,
  formatCpf,
  formatPhone,
  type PatientRowShape,
} from "./PatientListRow";

type Props = {
  patient: PatientRowShape;
  onRemove: (p: PatientRowShape) => void;
  onToggleActive: (p: PatientRowShape) => void;
};

/**
 * O paciente no celular. A tabela deixava quatro ações de 28px enfileiradas na borda direita, e
 * bastava um pouco mais de dado pra virar rolagem lateral — no celular, ação que exige rolar de lado
 * é ação invisível.
 *
 * Aqui o cartão inteiro abre a ficha, e o que é destrutivo ou secundário fica no menu, com rótulo
 * escrito em vez de só ícone.
 */
export function PatientListCard({ patient: p, onRemove, onToggleActive }: Props) {
  const navigate = useNavigate();
  const cor = getPatientColor(p.name);
  const apoio = [formatCpf(p.document), formatPhone(p.phone)].filter(Boolean).join(" • ");

  return (
    <li className={cn("flex items-start gap-3 px-1 py-3", !p.active && "opacity-60")}>
      <button
        type="button"
        onClick={() => navigate(`/pacientes/${p.id}`)}
        className="flex min-h-11 min-w-0 flex-1 items-start gap-3 text-left"
      >
        {p.avatarUrl ? (
          <img src={p.avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white"
            style={{ background: patientGradient(cor) }}
          >
            {p.name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-medium leading-snug text-zinc-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-foreground">
            {p.name}
          </span>
          {apoio && (
            <span className="block truncate text-xs text-zinc-500 dark:text-muted-foreground">
              {apoio}
            </span>
          )}
          <span
            className={cn(
              "mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
              p.active
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                : "bg-zinc-100 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
            )}
          >
            {p.active ? "Ativo" : "Inativo"}
          </span>
        </span>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
            aria-label={`Ações de ${p.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[190px] border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card"
        >
          <DropdownMenuItem
            onClick={() => navigate(`/pacientes/${p.id}`)}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
          >
            <ExternalLink className="h-4 w-4 text-zinc-500 dark:text-muted-foreground" />
            Ver ficha
          </DropdownMenuItem>
          {p.phone && (
            <DropdownMenuItem asChild className="cursor-pointer rounded-md px-2.5 py-2 text-sm">
              <a
                href={buildWhatsAppUrl(p.phone, p.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Enviar WhatsApp
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => onToggleActive(p)}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
          >
            {p.active ? (
              <Ban className="h-4 w-4 text-amber-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            )}
            {p.active ? "Inativar paciente" : "Reativar paciente"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onRemove(p)}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-red-600 dark:text-red-400"
          >
            <Trash2 className="h-4 w-4" />
            Excluir paciente
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
